import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/msalConfig";
import "./Login.css";
import { saveAuthData } from "./../utils/auth";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://hrpsc.swisstekaluminium.com";

function Login() {
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const redirectHandled = useRef(false);

  // =====================================
  // ADMIN / SUPER ADMIN LOGIN
  // Role is decided server-side from the account itself - this same
  // form works for both, the backend returns whichever role the
  // account actually has.
  // =====================================
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, {
        username,
        password,
      });

      saveAuthData(res.data);

      await axios.post(
        `${API_BASE}/api/history`,
        {
          module: "Authentication",
          action: "Admin Login",
          description: `${res.data.username} (${res.data.role}) logged into the system`,
          username: res.data.username,
          role: res.data.role,
        },
        {
          headers: { Authorization: `Bearer ${res.data.token}` },
        }
      );

      navigate("/dashboard");
    } catch (err) {
      alert(err.response?.data?.message || "Invalid Login");
    } finally {
      setLoading(false);
    }
  };

  // =====================================
  // EMPLOYEE (MICROSOFT) LOGIN - REDIRECT FLOW
  // Instead of a popup, the whole tab navigates to Microsoft and
  // back to this same page. We finish the login on the way back
  // inside the useEffect below (handleRedirectPromise).
  // =====================================
  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
      // Browser navigates away here - code after this line does not run
      // until the user comes back from Microsoft.
    } catch (err) {
      console.error("MICROSOFT REDIRECT START ERROR:", err);
      alert(err.message || "Could not start Microsoft sign-in.");
      setMsLoading(false);
    }
  };

  // Runs once when the page loads - including right after Microsoft
  // redirects the user back here with the login result in the URL.
  useEffect(() => {
    const completeMicrosoftLogin = async () => {
      if (redirectHandled.current) return;
      redirectHandled.current = true;

      try {
        const response = await instance.handleRedirectPromise();

        // response is null on a normal page load (no redirect just happened)
        if (!response) return;

        setMsLoading(true);

        const idToken = response.idToken;

        const res = await axios.post(`${API_BASE}/api/auth/microsoft-login`, {
          idToken,
        });

        saveAuthData(res.data);

        await axios.post(
          `${API_BASE}/api/history`,
          {
            module: "Authentication",
            action: "Employee Login",
            description: `${res.data.username} logged in with Microsoft`,
            username: res.data.username,
            role: res.data.role,
          },
          {
            headers: { Authorization: `Bearer ${res.data.token}` },
          }
        );

        navigate("/dashboard");
      } catch (err) {
        console.error("MICROSOFT LOGIN ERROR:", err);
        console.error("ERROR NAME:", err.name);
        console.error("ERROR MESSAGE:", err.message);
        console.error("ERROR CODE:", err.errorCode);
        console.error("RESPONSE DATA:", err.response?.data);
        alert(
          err.response?.data?.message ||
            err.errorCode ||
            err.message ||
            "Microsoft sign-in failed. Please try again."
        );
      } finally {
        setMsLoading(false);
      }
    };

    completeMicrosoftLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="brand-logo">
          <img src="/swisstek-logo.png" alt="Swisstek Aluminium" />
        </div>

        <h2 className="app-title">Company Dashboard</h2>
        <p className="subtitle">Secure and smart company management system</p>

        {/* ---------- ADMIN / SUPER ADMIN LOGIN ---------- */}
        <div className="login-section">
          <h3>Admin Login</h3>
          <p className="section-hint">
            Admins and Super Admins can sign in using username and password.
          </p>

          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Admin Email / Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />

            <input
              type="password"
              placeholder="Admin Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <button type="submit" className="admin-btn" disabled={loading}>
              {loading ? "Signing In..." : "Admin Sign In"}
            </button>
          </form>
        </div>

        <div className="divider">
          <span>OR</span>
        </div>

        {/* ---------- EMPLOYEE (MICROSOFT) LOGIN ---------- */}
        <div className="login-section">
          <h3>Employee Login</h3>
          <p className="section-hint">
            Employees must use their Swisstek Microsoft company email to sign in.
          </p>

          <button
            type="button"
            className="ms-btn"
            onClick={handleMicrosoftLogin}
            disabled={msLoading}
          >
            {msLoading ? "Signing In..." : "Sign in with Microsoft"}
          </button>
        </div>

        <div className="auth-links">
          <span>
            Need help? <a href="/contact-admin">Contact Admin</a>
          </span>
        </div>
      </div>
    </div>
  );
}

export default Login;
